import {Dialog, DialogActions, DialogContent, DialogTitle, Grid2 as Grid, Grid2} from "@mui/material";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {useModelState} from "@/lib/modelstate";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import * as React from "react";
import {useMemo} from "react";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {CommonI18N, CompanyI18N, UserI18N} from "@/translations";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {AccessLockDTO, CompanyDTO, CompanyStatisticsDTO} from "@/api/data-contracts";
import {Constants} from "@/api/constants";
import {companyApi} from "@/boxhub-api";
import {useDialogController} from "@/lib/dialog-controller";
import NumberInputField from "@/lib/components/inputfields/number-input-field";

export type BlockingCompanyDialogProps = {
    open: boolean;
    close: () => void;
    onSuccess?: () => void;
    company?: CompanyDTO;
    companyStatistics?: CompanyStatisticsDTO;
};

type BlockingCompanyValidation = Pick<AccessLockDTO, "lockedReason">;

const useSchema = () => {
    const { t } = useTranslation();
    return useMemo(() => {
        const schema = yup.object().shape({
            lockedReason: yup
                .string()
                .optional()
                .nullable()
                .max(
                    Constants.REASON_LENGTH_MAX,
                        t(CompanyI18N.blocking.validation.reasonMax, {max: Constants.REASON_LENGTH_MAX})
                )
        });
        const typed = schema as yup.ObjectSchema<BlockingCompanyValidation>;
        return typed as yup.ObjectSchema<AccessLockDTO>;
    }, [t]);
};

export const BlockingCompanyDialog = ({
    open,
    close,
    onSuccess,
    company,
    companyStatistics
}: BlockingCompanyDialogProps) => {
    const { t } = useTranslation();
    const [DialogNode, showDialog] = useDialogController();
    const schema = useSchema();
    const { model, errors, onChange, save, validate } = useModelState<AccessLockDTO, number>({
        schema,
        id: company?.id,
        updater: companyApi.lockCompany
    });

    const handleConfirmBlocking = () => {
        save()?.then(() => {
            close();
            onSuccess?.();
        });
    };

    const salutationOfCEO = useMemo(() => {
        const salutation = company?.contactPerson?.salutation;
        if (salutation) {
            return t(CommonI18N.enum.salutation[salutation]);
        } else {
            return "";
        }
    }, [t, company]);

    const countryOfCompany = useMemo(() => {
        const country = company?.companyAddress?.country;
        if (country) {
            return t(CommonI18N.enum.country[country]);
        } else {
            return "";
        }
    }, [t, company]);

    const ceoFirstName = useMemo(() => company?.contactPerson?.firstName ?? "", [company]);

    const ceoLastName = useMemo(() => company?.contactPerson?.lastName ?? "", [company]);

    const sumOpenRequests = useMemo(
        () =>
            (companyStatistics?.numberOfCreatedRequests ?? 0) +
            (companyStatistics?.numberOfPublishedRequests ?? 0) +
            (companyStatistics?.numberOfToBeConfirmedRequests ?? 0),
        [
            companyStatistics?.numberOfCreatedRequests,
            companyStatistics?.numberOfPublishedRequests,
            companyStatistics?.numberOfToBeConfirmedRequests
        ]
    );

    const handleBlockClick = () => {
        if (validate()) {
            showDialog((open, close) => (
                <ConfirmationDialog
                    open={open}
                    onClose={close}
                    onConfirm={handleConfirmBlocking}
                    title={t(CompanyI18N.blocking.dialog.confirmTitle, {companyName: company?.name})}
                    message={t(CompanyI18N.blocking.dialog.confirmMessage, {companyName: company?.name})}
                    confirmButtonText={t(CommonI18N.message.yes)}
                    cancelButtonText={t(CommonI18N.message.no)}
                />
            ));
        }
    };

    return (
        <>
            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>{t(CompanyI18N.blocking.dialog.title, {companyName: company?.name})}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} padding={2} width="100%">
                        <Grid width="100%">
                            <StringInputField
                                value={company?.name}
                                label={t(CompanyI18N.form.field.name)}
                                disabled
                            />
                        </Grid>
                        <Grid width="100%">
                            <StringInputField
                                    label={t(CompanyI18N.form.field.street)}
                                value={[company?.companyAddress?.street, company?.companyAddress?.houseNumber]
                                    .filter(Boolean)
                                    .join(" ")}
                                disabled
                            />
                        </Grid>
                        <Grid container spacing={2} width="100%">
                            <Grid size={{ xs: 6 }}>
                                <StringInputField
                                        label={t(CompanyI18N.form.field.postalCode)}
                                    value={company?.companyAddress?.zip}
                                    disabled
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <StringInputField
                                        label={t(CompanyI18N.form.field.city)}
                                    value={company?.companyAddress?.city}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                        <Grid width="100%">
                            <StringInputField
                                    label={t(CompanyI18N.form.field.country)}
                                value={countryOfCompany}
                                disabled
                            />
                        </Grid>
                        <Grid container spacing={2} width="100%">
                            <Grid size={{ xs: 2 }}>
                                <StringInputField
                                        label={t(UserI18N.form.field.salutation)}
                                    value={salutationOfCEO}
                                    disabled
                                />
                            </Grid>
                            <Grid size={{ xs: 5 }}>
                                <StringInputField
                                        label={t(UserI18N.form.field.firstName)}
                                    value={ceoFirstName}
                                    disabled
                                />
                            </Grid>
                            <Grid size={{ xs: 5 }}>
                                <StringInputField label={t(UserI18N.form.field.lastName)} value={ceoLastName} disabled/>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} width="100%">
                            <Grid size={{ xs: 6 }}>
                                <NumberInputField
                                    name="sumOpenRequests"
                                    label={t(CompanyI18N.blocking.field.sumOpenRequests)}
                                    value={sumOpenRequests}
                                    disabled
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <NumberInputField
                                    name="sumOpenRequests"
                                    label={t(CompanyI18N.blocking.field.sumSentQuotes)}
                                    value={companyStatistics?.numberOfSubmittedQuotes ?? 0}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                        <Grid width="100%">
                            <StringInputField
                                name="lockedReason"
                                label={t(CompanyI18N.blocking.field.reason)}
                                value={model?.lockedReason}
                                error={errors?.lockedReason}
                                onChange={onChange}
                                maxChars={Constants.REASON_LENGTH_MAX}
                                multiline
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid2 container direction="row" flexWrap="nowrap" spacing={2} padding={1}>
                        <Grid2>
                            <ButtonEx label={t(CommonI18N.action.cancel)} color="secondary" onClick={close}/>
                        </Grid2>
                        <Grid2>
                            <ButtonEx
                                    label={t(CompanyI18N.blocking.action.block)}
                                    color="primary"
                                    onClick={handleBlockClick}
                            />
                        </Grid2>
                    </Grid2>
                </DialogActions>
            </Dialog>
            <DialogNode />
        </>
    );
};
